import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ShieldCheck } from "lucide-react";
import { ADMIN_DASHBOARD_URL } from "@/constants/adminRoutes";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(username, password, needsTotp ? totpCode : undefined);
      if (res?.requires_totp) {
        setNeedsTotp(true);
        setLoading(false);
        return;
      }
      navigate(ADMIN_DASHBOARD_URL);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sarathi-background flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="https://customer-assets.emergentagent.com/job_ev-mobility-2/artifacts/46iqyr35_logo-orange-white.svg" alt="Sarathi-X" className="h-8" />
            <span className="font-outfit text-2xl font-bold text-white">Sarathi-X</span>
          </div>
          <h1 className="font-outfit text-2xl font-bold text-white">Admin Panel</h1>
          <p className="text-sarathi-text-muted text-sm mt-1">Sign in to manage bookings</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-sarathi-surface border border-white/10 rounded-2xl p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg px-4 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          {!needsTotp ? (
            <>
              <div>
                <label className="block text-sarathi-text-muted text-sm mb-1.5">Username</label>
                <Input value={username} onChange={e => setUsername(e.target.value)} type="text" placeholder="Username"
                  className="bg-sarathi-background border-white/10 text-white rounded-xl h-11" data-testid="login-username" />
              </div>
              <div>
                <label className="block text-sarathi-text-muted text-sm mb-1.5">Password</label>
                <Input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="Password"
                  className="bg-sarathi-background border-white/10 text-white rounded-xl h-11" data-testid="login-password" />
              </div>
            </>
          ) : (
            <div className="text-center space-y-3">
              <ShieldCheck className="w-10 h-10 text-sarathi-primary mx-auto" />
              <p className="text-white font-medium">Two-Factor Authentication</p>
              <p className="text-sarathi-text-muted text-xs">Enter the 6-digit code from your authenticator app</p>
              <Input value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000" maxLength={6} autoFocus
                className="bg-sarathi-background border-white/10 text-white rounded-xl h-12 text-center text-2xl font-mono tracking-[0.5em]" data-testid="login-totp" />
            </div>
          )}

          <Button type="submit" disabled={loading} className="w-full bg-sarathi-primary hover:bg-sarathi-primary-hover text-white h-11 rounded-xl" data-testid="login-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {!loading && needsTotp ? "Verify" : null}
            {!loading && !needsTotp ? "Sign In" : null}
          </Button>

          {needsTotp && (
            <button type="button" onClick={() => { setNeedsTotp(false); setTotpCode(""); setError(""); }}
              className="text-sarathi-text-muted text-xs hover:text-white transition-colors w-full text-center">
              Back to login
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
